// Fachlogik des Freelancer-Portals (ERP/CRM-Plan Abschnitt 7 „Workflow"
// Schritte 2–3 und 3.4 — Phase D3).
//
// Hier steht ausschliesslich, was sich OHNE Datenbank entscheiden laesst:
// welchen Zustand ein Zugang hat, wie die eigenen Auftraege gebuendelt werden,
// welche Knoepfe an einem Auftrag erscheinen duerfen, wie die Nachweis-Ampel
// sortiert ist. Der unreine Teil (Laden, RPC-Aufrufe) liegt in
// freelancerPortalService.ts.
//
// Die Uebergangsmatrix wird NICHT nachgebaut: sie steht in assignmentLogic.ts
// (`ASSIGNMENT_TRANSITIONS`) und dort ist sie der Spiegel von
// public.set_assignment_status (supabase/freelancer_assignments.sql, 6d). Hier
// wird sie nur mit dem Handelnden 'freelancer' AUFGERUFEN.
//
// Kein jsdom im Projekt (Standing Decision) — deshalb pur und mit Vitest
// geprueft.
//
// RLS, View und Policies: supabase/freelancer_portal.sql (MIGRATIONS.md Nr. 20).

import {
    AssignmentStatus,
    InspectionAssignment,
    erlaubteFolgestatus,
    sortiereAssignments,
} from './assignmentLogic';
import {
    FreelancerQualification,
    QualiStatus,
    heuteIso,
    istPflichtQuali,
    qualiStatus,
} from './freelancerLogic';

// ─── Zugang ─────────────────────────────────────────────────────────────────

/**
 * Zustand des Portal-Zugangs.
 *
 *   ok             — Login ist mit einem Stammsatz verknuepft, Status aktiv
 *   pausiert       — verknuepft, aber vom Innendienst pausiert
 *   gesperrt       — verknuepft, aber gesperrt
 *   nicht_verknuepft — es gibt keinen Stammsatz zu diesem Login
 *
 * `nicht_verknuepft` ist KEIN Fehler: die Rolle wird vergeben, bevor der
 * Innendienst die Verknuepfung setzt (freelancers.profile_user_id, D3). Genau
 * dieser Zustand traegt im Portal die Meldung „Ihr Zugang ist noch nicht
 * freigeschaltet" — eine Fehlerseite waere an dieser Stelle falsch.
 */
export type PortalZugang = 'ok' | 'pausiert' | 'gesperrt' | 'nicht_verknuepft';

export interface PortalZugangsInfo {
    zustand: PortalZugang;
    /** Deutscher Klartext fuer die Oberflaeche. '' bei `ok`. */
    hinweis: string;
    /** Duerfen Auftraege angenommen werden? Nur im Zustand `ok`. */
    darfHandeln: boolean;
}

/**
 * Zugangszustand aus dem eigenen Stammsatz ableiten.
 *
 * `null`/`undefined` heisst „kein Stammsatz gefunden". Das ist der Normalfall
 * unmittelbar nach der Rollenvergabe und muss ohne Ladefehler dargestellt
 * werden.
 */
export const portalZugang = (
    f: { status?: string } | null | undefined,
): PortalZugangsInfo => {
    if (!f) {
        return {
            zustand: 'nicht_verknuepft',
            hinweis:
                'Ihr Zugang ist noch nicht freigeschaltet. Der Innendienst muss Ihr '
                + 'Benutzerkonto mit Ihrem Freelancer-Stammsatz verknüpfen. '
                + 'Bitte wenden Sie sich an Ihre Ansprechpartnerin oder Ihren Ansprechpartner.',
            darfHandeln: false,
        };
    }
    if (f.status === 'gesperrt') {
        return {
            zustand: 'gesperrt',
            hinweis:
                'Ihr Zugang ist derzeit gesperrt. Neue Aufträge können nicht angenommen '
                + 'werden. Bitte wenden Sie sich an den Innendienst.',
            darfHandeln: false,
        };
    }
    if (f.status === 'pausiert') {
        return {
            zustand: 'pausiert',
            hinweis:
                'Ihr Zugang ist pausiert. Bestehende Aufträge bleiben sichtbar, neue '
                + 'Angebote erhalten Sie vorerst nicht.',
            darfHandeln: false,
        };
    }
    return { zustand: 'ok', hinweis: '', darfHandeln: true };
};

// ─── Auftraege ──────────────────────────────────────────────────────────────

/**
 * Welche Statuswechsel darf der Freelancer an DIESEM Auftrag ausloesen?
 *
 * Reiner Aufruf von `erlaubteFolgestatus('freelancer', …)`. Die zweite
 * Bedingung ist der Zugang: ein gesperrter Freelancer sieht seine Angebote
 * weiterhin, kann sie aber nicht mehr annehmen — sonst entstuende eine Zusage,
 * die der Innendienst gerade unterbunden hat.
 */
export const freelancerAktionen = (
    a: Pick<InspectionAssignment, 'status'> | null | undefined,
    zugang: PortalZugangsInfo,
): AssignmentStatus[] => {
    if (!a || !zugang.darfHandeln) return [];
    return erlaubteFolgestatus('freelancer', a.status);
};

/** Wartet dieser Auftrag auf eine Entscheidung des Freelancers? */
export const wartetAufEntscheidung = (
    a: Pick<InspectionAssignment, 'status'> | null | undefined,
): boolean => !!a && a.status === 'angeboten';

/**
 * Zustaende, die im Portal unter „laufend" stehen: zugesagt oder in
 * Bearbeitung, aber noch nicht abgeschlossen. Bewusst NICHT identisch mit
 * `AKTIVE_ASSIGNMENT_STATUS` aus assignmentLogic — dort geht es um die Frage,
 * ob ein Zeitraum BELEGT ist (inkl. 'angeboten'), hier um die Frage, was der
 * Freelancer bereits zugesagt hat.
 */
export const LAUFENDE_PORTAL_STATUS: AssignmentStatus[] = [
    'angenommen',
    'durchgefuehrt',
    'bericht_eingereicht',
    'nachbesserung',
];

export interface PortalAuftragsGruppen {
    /** Warten auf Annahme oder Ablehnung — die Arbeitsliste. */
    angebote: InspectionAssignment[];
    /** Zugesagt und noch nicht abgeschlossen. */
    laufend: InspectionAssignment[];
    /** Abgeschlossen oder folgenlos beendet — das Archiv. */
    erledigt: InspectionAssignment[];
}

/**
 * Auftraege in die drei Buendel des Portals sortieren.
 *
 * Innerhalb jedes Buendels gilt die Reihenfolge aus `sortiereAssignments`
 * (Beginn absteigend) — dieselbe wie in der Freelancer-Akte des Innendienstes,
 * damit beide Seiten dieselbe Liste in derselben Ordnung sehen.
 */
export const gruppiereAuftraege = (
    assignments: InspectionAssignment[] | null | undefined,
): PortalAuftragsGruppen => {
    const angebote: InspectionAssignment[] = [];
    const laufend: InspectionAssignment[] = [];
    const erledigt: InspectionAssignment[] = [];

    for (const a of assignments || []) {
        if (!a) continue;
        if (a.status === 'angeboten') angebote.push(a);
        else if (LAUFENDE_PORTAL_STATUS.includes(a.status)) laufend.push(a);
        else erledigt.push(a);
    }

    return {
        angebote: sortiereAssignments(angebote),
        laufend: sortiereAssignments(laufend),
        erledigt: sortiereAssignments(erledigt),
    };
};

/** Zahl der Auftraege, die auf eine Entscheidung warten — das Abzeichen. */
export const offeneAngeboteZahl = (
    assignments: InspectionAssignment[] | null | undefined,
): number => (assignments || []).filter(wartetAufEntscheidung).length;

// ─── Projekte ───────────────────────────────────────────────────────────────

/**
 * Projektkopf aus `freelancer_projects_v` (supabase/freelancer_portal.sql,
 * Abschnitt 3). Genau diese fuenf Felder — mehr liefert die View nicht, und
 * mehr braucht das Portal nicht.
 */
export interface PortalProjekt {
    id: string;
    name: string;
    projectNumber?: string;
    /** Freitext-Adresse aus der Projektakte. Fehlt oft. */
    address?: string;
    /** Zustaendiger Koordinator des Innendienstes. */
    coordinatorName?: string;
}

/** Eine Adresse fuer die Anzeige; '' wenn keine gepflegt ist. */
export const projektAdresse = (p: PortalProjekt | null | undefined): string =>
    (p?.address || '').trim();

/**
 * Projekte in Anzeigereihenfolge: nach Name, deutsch sortiert. Die View
 * liefert keine Sortierung, und eine wechselnde Reihenfolge zwischen zwei
 * Ladevorgaengen liest sich wie ein Datenfehler.
 */
export const sortiereProjekte = (
    projekte: PortalProjekt[] | null | undefined,
): PortalProjekt[] =>
    [...(projekte || [])].sort((a, b) => (a?.name || '').localeCompare(b?.name || '', 'de'));

/**
 * Auftraege je Projekt buendeln — die Detailansicht eines Projekts zeigt, was
 * dort ansteht. Projekte ohne Auftrag bekommen eine leere Liste, nicht
 * `undefined`: die Oberflaeche soll nicht zwischen „keine" und „nicht geladen"
 * unterscheiden muessen.
 */
export const auftraegeJeProjekt = (
    projekte: PortalProjekt[] | null | undefined,
    assignments: InspectionAssignment[] | null | undefined,
): Map<string, InspectionAssignment[]> => {
    const map = new Map<string, InspectionAssignment[]>();
    for (const p of projekte || []) {
        if (p?.id) map.set(p.id, []);
    }
    for (const a of assignments || []) {
        if (!a?.projectId) continue;
        const vorhanden = map.get(a.projectId);
        if (vorhanden) vorhanden.push(a);
        else map.set(a.projectId, [a]);
    }
    for (const [id, liste] of map) map.set(id, sortiereAssignments(liste));
    return map;
};

// ─── Nachweise ──────────────────────────────────────────────────────────────

export interface PortalNachweis {
    quali: FreelancerQualification;
    status: QualiStatus;
    pflicht: boolean;
}

/** Reihenfolge der Ampel: das Dringendste zuerst. */
const AMPEL_RANG: Record<QualiStatus, number> = {
    abgelaufen: 0,
    laeuft_ab: 1,
    ohne_datum: 2,
    gueltig: 3,
};

/**
 * Nachweise fuer die Portal-Ansicht aufbereiten: Ampelstatus je Zeile,
 * Pflichtkennzeichen, und sortiert nach Dringlichkeit (abgelaufen zuerst,
 * darin Pflichtnachweise vor freiwilligen, darin nach Ablaufdatum).
 *
 * Die Ampel selbst kommt unveraendert aus `qualiStatus` — im Portal gilt
 * dieselbe Regel wie in der Akte des Innendienstes. Zwei Bewertungen desselben
 * Ablaufdatums waeren ein Fehler, der erst im Streitfall auffiele.
 */
export const portalNachweise = (
    qualis: FreelancerQualification[] | null | undefined,
    heute: string = heuteIso(),
): PortalNachweis[] =>
    (qualis || [])
        .filter(Boolean)
        .map((q) => ({
            quali: q,
            status: qualiStatus(q, heute),
            pflicht: istPflichtQuali(q.typ),
        }))
        .sort((a, b) => {
            const rang = AMPEL_RANG[a.status] - AMPEL_RANG[b.status];
            if (rang !== 0) return rang;
            if (a.pflicht !== b.pflicht) return a.pflicht ? -1 : 1;
            return (a.quali.gueltigBis || '9999-12-31').localeCompare(
                b.quali.gueltigBis || '9999-12-31',
            );
        });

/**
 * Kurzfassung fuer den Kopf der Nachweis-Karte: „2 abgelaufen, 1 läuft ab".
 * '' wenn alles in Ordnung ist — dann steht dort bewusst nichts statt einer
 * Entwarnung, die niemand liest.
 */
export const nachweisWarnung = (nachweise: PortalNachweis[] | null | undefined): string => {
    let abgelaufen = 0;
    let laeuftAb = 0;
    for (const n of nachweise || []) {
        if (n?.status === 'abgelaufen') abgelaufen += 1;
        else if (n?.status === 'laeuft_ab') laeuftAb += 1;
    }
    const teile: string[] = [];
    if (abgelaufen > 0) {
        teile.push(abgelaufen === 1 ? '1 Nachweis abgelaufen' : `${abgelaufen} Nachweise abgelaufen`);
    }
    if (laeuftAb > 0) {
        teile.push(laeuftAb === 1 ? '1 Nachweis läuft ab' : `${laeuftAb} Nachweise laufen ab`);
    }
    return teile.join(', ');
};
